// -------------------------------
// src/pages/Properties.js
// -------------------------------
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getApiUrl } from '../config/api';
import authFetch from '../utils/authFetch';

/**
 * "Properties" page – lists every active property for the current user's organization.
 * Authenticates via JWT and calls: GET /api/v1/property/list
 */
const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Utility: nicely format the date_added field (fallback to '-')
  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return iso;
    }
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const res = await authFetch(getApiUrl('/api/v1/property/list'));

        if (!res.ok) throw new Error(`Server responded with ${res.status}`);

        const json = await res.json();
        setProperties(json.properties || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  return (
    <Layout pageTitle="Your Properties">
      <div className="properties-container space-y-4">
        {properties.length > 0 && !loading && !error && (
          <p className="text-sm text-gray-600">
            {properties.length} active propert{properties.length > 1 ? 'ies' : 'y'}
          </p>
        )}

        {loading && <p className="text-gray-600">Loading…</p>}
        {error && <p className="text-red-600">{error}</p>}
        {!loading && !error && properties.length === 0 && (
          <p className="text-gray-600">No active properties found.</p>
        )}

        {!loading && !error && properties.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {properties.map((prop, idx) => (
              <article
                key={`${prop.name}-${idx}`}
                className="flex flex-col bg-white rounded-2xl shadow p-5 hover:shadow-md transition-shadow"
              >
                <h3 className="text-lg font-medium mb-1 text-zenrent-green-700">
                  {prop.name}
                </h3>
                <p className="text-sm text-gray-800 mb-2 leading-snug">
                  {prop.address}
                </p>

                <p className="text-sm text-gray-600 flex-1">
                  {prop.description || 'No description provided.'}
                </p>

                <footer className="mt-4 text-xs text-gray-500">
                  Added {formatDate(prop.date_added)}
                </footer>
              </article>
            ))}
          </div>
        )}

        {/* Add Property button */}
        <div className="flex justify-center mt-8">
          <button
            onClick={() => navigate('/addproperty')}
            className="bg-[#4CAF50] hover:bg-[#45a049] text-white font-medium py-2 px-6 rounded-lg shadow-sm transition-colors flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Property
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default Properties;